import {
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CountryToken, EnvToken } from '@app/core/env.tokens';
import { buildStaticPaths } from '@app/routes/static-paths';
import { isPlatformBrowser } from '@angular/common';

import { Blog } from '@models/blog.model';
import { BlogService } from '@services/http/blog.service';
import { SEOService } from '@services/logic/seo.service';

import { Router, RouterLink } from '@angular/router';

import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { ScrollProgressBarDirective } from '@app/directives/scroll-progress-bar-directive';
import { FooterComponent } from '@components/shared/footer/footer.component';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const CARD_HEIGHT = 230;
export const CARD_WIDTH = 380;

@Component({
  imports: [
    CommonModule,
    NgOptimizedImage,
    TranslatePipe,
    ScrollProgressBarDirective,
    RouterLink,
    FooterComponent,
  ],
  providers: [BlogService, SEOService, TranslatePipe],
  selector: 'app-blog',
  templateUrl: './blog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./blog.component.scss'],
})
export class BlogComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly staticPaths = buildStaticPaths(inject(CountryToken));

  private destroy$ = new Subject<void>();
  imgBasePath = this.env.cloudFrontImgUrl + '/private/compressed';
  activeHeaderImg = `${this.imgBasePath}/lp/webp/vertical+hero+images/handyman_22_11zon.webp`;
  blogList: Blog[] = [];
  filteredBlogList: Blog[] = [];
  defaultImage = '/assets/svg/logo.svg';
  blogCategoryList: { catId: number; catName: string; img: string }[] = [];
  selectedFilteredCatId?: number;
  isFetching: boolean = true;
  schema: any;

  constructor(
    private blogService: BlogService,
    private route: Router,
    private seoService: SEOService,
    private t: TranslatePipe,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit() {
    this.setMeta();
    this.fetchBlogPosts();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchBlogPosts() {
    this.isFetching = true;
    this.blogService
      .fetchAllBlogPost()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Ensure we have a proper copy of the blog list with all properties
          this.blogList = [...res.blogList];
          this.filteredBlogList = [...res.blogList];

          this.blogCategoryList = res.blogCats;
          this.isFetching = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isFetching = false;
          this.blogList = [];
          this.filteredBlogList = [];
          this.cdr.markForCheck();
        },
      });
  }

  setMeta() {
    this.seoService.setPageMeta({
      metaTitle: this.t.transform('COMPONENTS.BLOG.META_TITLE'),
      metaDesc: this.t.transform('COMPONENTS.BLOG.META_DESC'),
      // `Router.url` is a PATH, so it published `<link rel="canonical"
      // href="/blog">` — a relative canonical Google resolves against whatever
      // host served the page, including the NL domain that has no blog.
      // Resolved from the route table, not hardcoded: one build, many hosts.
      canonicalUrl: `${this.env.domain}/${this.staticPaths.blog}`,
      img: this.activeHeaderImg,
    });
  }

  public goTo(slug: string) {
    return this.route.navigate([`/blog/${slug}`]);
  }

  public selectableBlogCat(blogCatId: number) {
    // If clicking the same category, reset to show all blogs
    if (this.selectedFilteredCatId === blogCatId) {
      this.selectedFilteredCatId = undefined;
      this.filteredBlogList = [...this.blogList];
      this.activeHeaderImg = `${this.imgBasePath}/lp/webp/vertical+hero+images/handyman_22_11zon.webp`;
    } else {
      this.selectedFilteredCatId = blogCatId;
      // Filter blogs by category, ensuring all properties are preserved
      this.filteredBlogList = this.blogList.filter((blog) =>
        blog.categories?.includes(blogCatId),
      );
      this.setHeaderImg();
    }
  }

  private setHeaderImg() {
    if (this.blogCategoryList?.length && this.selectedFilteredCatId) {
      const selectedCat = this.blogCategoryList.find(
        (x) => x.catId === this.selectedFilteredCatId,
      );
      if (selectedCat && selectedCat.img) {
        this.activeHeaderImg = selectedCat.img;
      }
    }
  }

  public resetFilter() {
    this.selectedFilteredCatId = undefined;
    this.filteredBlogList = [...this.blogList];
    this.activeHeaderImg = `${this.imgBasePath}/lp/webp/vertical+hero+images/handyman_22_11zon.webp`;
  }

  protected readonly CARD_HEIGHT = CARD_HEIGHT;
  protected readonly CARD_WIDTH = CARD_WIDTH;

  // Helper method to strip HTML tags and get plain text
  getPlainTextExcerpt(htmlExcerpt: string): string {
    if (!htmlExcerpt) return '';

    // Check if we're running in the browser
    if (isPlatformBrowser(this.platformId)) {
      // Create a temporary div to parse HTML (browser only)
      const tempDiv = this.document.createElement('div');
      tempDiv.innerHTML = htmlExcerpt;

      // Get text content and clean up
      return tempDiv.textContent || tempDiv.innerText || '';
    } else {
      // SSR fallback: use regex to strip HTML tags
      return htmlExcerpt
        .replace(/<[^>]*>/g, '') // Remove all HTML tags
        .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
        .replace(/&hellip;/g, '...') // Replace HTML ellipsis
        .replace(/&amp;/g, '&') // Replace HTML ampersand
        .replace(/&lt;/g, '<') // Replace HTML less than
        .replace(/&gt;/g, '>') // Replace HTML greater than
        .replace(/&quot;/g, '"') // Replace HTML quotes
        .replace(/&#39;/g, "'") // Replace HTML apostrophe
        .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
        .trim(); // Remove leading/trailing whitespace
    }
  }
}
